import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import NumberField from "../components/NumberField";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  getDeliveryZones,
  createDeliveryZone,
  updateDeliveryZone,
  deleteDeliveryZone,
} from "../api/deliveryZonesApi";

export default function DeliveryZones() {
  const [zones, setZones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingZone, setEditingZone] = useState<any | null>(null);
  const [form, setForm] = useState<{
  name: string;
  city: string;
  fee_dollars: number; // clearer name
  active: boolean;
}>({
  name: "",
  city: "",
  fee_dollars: 0,
  active: true,
});

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const data = await getDeliveryZones();
      setZones(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load delivery zones");
    } finally {
      setLoading(false);
    }
  };

const handleOpen = (zone?: any) => {
  if (zone) {
    setEditingZone(zone);
    setForm({
      name: zone.name,
      city: zone.city,
      fee_dollars: zone.fee_cents / 100, // ✅ convert cents → dollars (as number)
      active: zone.active,
    });
  } else {
    setEditingZone(null);
    setForm({ name: "", city: "", fee_dollars: 0, active: true });
  }
  setOpenDialog(true);
};

const handleSave = async () => {
  try {
    const payload = {
      name: form.name.trim(),
      city: form.city.trim(),
      fee_cents: Math.round(form.fee_dollars * 100), // ✅ convert back to cents
      active: form.active,
    };

    if (!payload.city) {
      alert("City is required");
      return;
    }

    if (editingZone) {
      await updateDeliveryZone(editingZone.id, payload);
    } else {
      await createDeliveryZone(payload);
    }

    setOpenDialog(false);
    await fetchZones();
  } catch (err) {
    console.error(err);
    alert("Failed to save delivery zone");
  }
};


  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this zone?")) return;
    await deleteDeliveryZone(id);
    await fetchZones();
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 5 }}>
      <Card>
        <CardHeader
          title="Delivery Zones"
          action={
            <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>
              New Zone
            </Button>
          }
        />
        <CardContent>
          {zones.length === 0 ? (
            <Typography>No zones found</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Fee ($)</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.map((z) => (
                  <TableRow key={z.id}>
                    <TableCell>{z.name}</TableCell>
                    <TableCell>{z.city}</TableCell>
                    <TableCell>${(z.fee_cents / 100).toFixed(2)}</TableCell>
                    <TableCell>{z.active ? "✅" : "❌"}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpen(z)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(z.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog for create/edit */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingZone ? "Edit Delivery Zone" : "New Delivery Zone"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Zone Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            sx={{ mb: 2 }}
          />
        <NumberField
  fullWidth
  label="Delivery Fee ($)"
  value={form.fee_dollars}
  min={0}
  clampEmptyTo={0}
  onValueChange={(v) => setForm({ ...form, fee_dollars: v ?? 0 })}
  sx={{ mb: 2 }}
  inputProps={{ step: "0.01" }}
/>


          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
